import React, { useEffect, useRef, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { Send, Hash } from "lucide-react";
import client from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card, Loading } from "../components/common";
import { AdSlot } from "../components/AdSlot";

export default function FantasyChat() {
  const { user } = useAuth();
  const [rooms, setRooms] = useState(null);
  const [room, setRoom] = useState("gameweek-lounge");
  const [msgs, setMsgs] = useState([]);
  const [body, setBody] = useState("");
  const endRef = useRef(null);

  useEffect(() => { client.get("/chat/rooms/list").then((r) => setRooms(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    let alive = true;
    const load = () => client.get(`/chat/${room}`).then((r) => { if (alive) setMsgs(r.data); }).catch(() => {});
    load();
    const t = setInterval(load, 3000);
    return () => { alive = false; clearInterval(t); };
  }, [room, user]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  if (user === null) return <Loading />;

  const send = async () => {
    if (!body.trim()) return;
    await client.post("/chat", { room, body });
    setBody("");
    const { data } = await client.get(`/chat/${room}`);
    setMsgs(data);
  };

  return (
    <div data-testid="fantasy-chat-page">
      <h1 className="font-head font-black text-3xl text-green mb-4">Fantasy Chat</h1>
      <div className="grid lg:grid-cols-4 gap-4">
        <Card className="p-2 lg:col-span-1 h-fit max-h-[520px] overflow-y-auto scrollbar-thin">
          <button onClick={() => setRoom("gameweek-lounge")} className={`w-full text-left px-3 py-2 rounded-sm text-sm font-semibold flex items-center gap-2 ${room === "gameweek-lounge" ? "bg-green text-cream" : "hover:bg-cream"}`} data-testid="room-lounge"><Hash size={14} /> Gameweek Lounge</button>
          <div className="text-[11px] uppercase tracking-wider text-ink/40 px-3 mt-3 mb-1">Club rooms</div>
          {rooms?.clubs.map((c) => (
            <button key={c.id} onClick={() => setRoom(c.id)} className={`w-full text-left px-3 py-1.5 rounded-sm text-sm flex items-center gap-2 ${room === c.id ? "bg-green text-cream" : "hover:bg-cream"}`} data-testid={`room-${c.id}`}><Hash size={13} /> {c.name}</button>
          ))}
        </Card>

        <div className="lg:col-span-2">
          <Card className="flex flex-col h-[520px]">
            <div className="flex-1 overflow-y-auto scrollbar-thin p-4 space-y-3">
              {msgs.length === 0 && <p className="text-sm text-ink/40 text-center mt-10">No messages yet. Start the conversation.</p>}
              {msgs.map((m) => (
                <div key={m.id} className={`flex ${m.author_id === user.id ? "justify-end" : ""}`} data-testid={`chat-msg-${m.id}`}>
                  <div className={`max-w-[75%] rounded-sm px-3 py-2 ${m.author_id === user.id ? "bg-green text-cream" : "bg-cream border border-pslborder"}`}>
                    <div className="text-[11px] font-bold opacity-70 mb-0.5">{m.username}</div>
                    <div className="text-sm">{m.body}</div>
                  </div>
                </div>
              ))}
              <div ref={endRef} />
            </div>
            <div className="p-3 border-t border-pslborder flex gap-2">
              {user ? (
                <>
                  <input value={body} onChange={(e) => setBody(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder={`Message #${room}`} className="flex-1 border border-pslborder rounded-sm p-2.5 text-sm focus:outline-none focus:border-gold" data-testid="chat-input" />
                  <button onClick={send} className="px-4 bg-gold text-ink rounded-sm font-bold hover:bg-green hover:text-cream transition-colors" data-testid="chat-send"><Send size={16} /></button>
                </>
              ) : (
                <div className="flex-1 text-center text-sm text-ink/60 py-1" data-testid="chat-signin-gate">Reading is free — <Link to="/signup" className="text-green font-bold">sign up</Link> to join the chat</div>
              )}
            </div>
          </Card>
          <p className="text-xs text-ink/40 mt-2">Usernames are public; phone numbers stay hidden. Super Admins & Media Updates can mute.</p>
        </div>

        <aside className="lg:col-span-1"><AdSlot id="fantasy.chat_sidebar" height={400} /></aside>
      </div>
    </div>
  );
}
